import { HttpClient } from "@angular/common/http";
import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { MenuItemForListDto } from "@dtos/menu-items/menu-items-for-list-dto";
import { CartService } from "@services/cart.service";
import { InventoryService } from "@services/inventory.service";
import { ErrorHandlerService } from "@services/error-handler.service";
import { RequestHandlerService } from "@services/request-handler.service";
import { TryResult } from "@app/utils/tryer";
import { lastValueFrom } from "rxjs";
import { environment } from '@envs/environment';

@Injectable({
    providedIn: 'root'
})
export class MenuItemsService {
    private readonly _menuItemsBaseUrl = environment.apiUrl + 'menuItems/';
    private readonly _valuesBaseUrl = environment.apiUrl + 'values/';
    private readonly _req = inject(RequestHandlerService);
    private readonly _http = inject(HttpClient);
    private readonly _inventory = inject(InventoryService);
    private readonly _cart = inject(CartService);
    private readonly _errorHandler = inject(ErrorHandlerService);

    isLoadingMenuItems = signal(false);
    isLoadingCategories = signal(false);
    isUploadingPhoto = signal(false);

    menuItems = signal<MenuItemForListDto[]>([]);
    textFilter = signal<string | undefined>('');
    adminTextFilter = signal<string | undefined>('');
    categoryFilters = signal<string[]>([]);
    private _menuItemsWithQuantities = computed(() => {
        const cartItems = this._cart.cartItems() ?? [];
        const outOfStockItems = this._inventory.outOfStockItems();
        return this.menuItems().map(menuItem => ({
            ...menuItem,
            quantity: cartItems.find(x => x.menuItem.id === menuItem.id)?.quantity ?? 0,
            isOutOfStock: outOfStockItems.some(x => x.menuItemId === menuItem.id),
        }));
    });

    displayMenuItems = computed(() =>
        filterMenuItems(this._menuItemsWithQuantities(), this.textFilter(), this.categoryFilters())
            .sort((a, b) => b.quantity - a.quantity)
    );

    adminMenuItems = computed(() =>
        filterMenuItems(this._menuItemsWithQuantities(), this.adminTextFilter(), [])
            .sort((a, b) => a.name.localeCompare(b.name))
    );

    categories = signal<string[]>([]);

    async loadMenuItemsAsync() {
        if (this.menuItems()?.length) return;

        const response = await this._req.get<MenuItemForListDto[]>(this._menuItemsBaseUrl, this.isLoadingMenuItems);
        if (response.type === 'error') {
            await this._errorHandler.handle(response.error, 'Could not load the menu. Please check your connection and try again.');
            return;
        }
        this.menuItems.set(response.data);
    }

    async updateMenuItem(id: number, menuItem: MenuItemForListDto) {
        return lastValueFrom(this._http.put<MenuItemForListDto>(this._menuItemsBaseUrl + id, menuItem));
    }

    async uploadPhoto(
        photo: File,
        type: 'Primary' | 'Thumbnail',
        busy: WritableSignal<boolean> = this.isUploadingPhoto,
    ): Promise<TryResult<string>> {
        const formData = new FormData();
        formData.append('photo', photo);
        formData.append('photoType', type);
        const response = await this._req.post<{ url: string }>(this._menuItemsBaseUrl + 'photo', formData, busy);
        return response.type === 'error' ? response : {type: 'success', data: response.data.url};
    }

    setMenuItemPhoto(menuItemId: number, type: 'Primary' | 'Thumbnail', url: string) {
        this.menuItems.update(items => items.map(item => {
            if (item.id !== menuItemId) return item;

            const photos = item.photos.some(x => x.type === type)
                ? item.photos.map(x => x.type === type ? {...x, url} : x)
                : [...item.photos, {id: 0, type, url}];

            return {...item, photos};
        }));
    }

    async loadMenuItemCategoriesAsync() {
        const response = await this._req.get<string[]>(
            this._valuesBaseUrl + 'menuItemCategories',
            this.isLoadingCategories,
        );
        if (response.type === 'error') return;
        this.categories.set(response.data);
    }

}

function filterMenuItems(
    menuItems: MenuItemForListDto[],
    textFilter: string | undefined,
    categoryFilters: string[],
): MenuItemForListDto[] {
    const text = textFilter?.trim().toLowerCase();
    let filtered = [...menuItems];

    if (text?.length) {
        filtered = filtered.filter(x =>
            x.name.toLowerCase().includes(text) || x.category.toLowerCase().includes(text)
        );
    }

    if (categoryFilters?.length) {
        filtered = filtered.filter(x => categoryFilters.includes(x.category));
    }

    return filtered;
}
